/**
 * Defera Miles: what a Mile is worth, and the rules for spending Miles on a
 * trip at checkout. Plain JS so every side shares it, the way
 * capital-checkout-integrity.js and faye-pricing.js are shared: the browser
 * (Review's Miles panel, the Rewards page, search results), Express (the
 * desk's account context, finance reports) and Deno (the checkout functions).
 *
 * A Mile is worth more on a Defera trip than as a gift card: 1.25¢ off a
 * booking, 1¢ toward an e-gift card. A gift card is cash leaving Defera; a
 * Mile spent on a trip is a reason to book again. Before 2026-10-05 both were
 * 1¢ and spending Miles on a trip went through a manual cart hand-off that no
 * customer ever completed (43 carts chose it, none became a booking).
 *
 * At checkout, Miles work like a discount the customer pays for with Miles:
 * the trip total falls by their value before the payment plan is built, so
 * Stripe and NMI only ever see a smaller plan. The checkout function deducts
 * the Miles before it charges the card and returns them if the charge fails;
 * a trip that is later cancelled gets them back (migration 162).
 *
 * Miles can cover up to 100% of a trip (founder, 2026-10-05). A trip they
 * cover entirely has nothing for a payment plan to collect, so it is booked
 * with Miles alone (server/miles-only-booking.js): no card, no plan. A trip
 * they cover only partly keeps at least MILES_MIN_PLAN_CENTS on the plan,
 * the smallest plan every rail can run at every deposit tier.
 */

/** What one Mile takes off a Defera trip, in cents. */
export const MILES_TRIP_CENTS_PER_MILE = 1.25;

/** What one Mile buys in e-gift cards, in cents (100 Miles = $1). */
export const MILES_GIFT_CARD_CENTS_PER_MILE = 1;

/** How much further a Mile goes on a trip than on a gift card, in percent (25). Copy quotes this, never a literal. */
export const MILES_TRIP_BONUS_PERCENT = Math.round((MILES_TRIP_CENTS_PER_MILE / MILES_GIFT_CARD_CENTS_PER_MILE - 1) * 100);

/**
 * What finance books for each outstanding Mile: the most a Mile can cost
 * Defera, which is its trip value.
 */
export const MILES_LIABILITY_CENTS_PER_MILE = MILES_TRIP_CENTS_PER_MILE;

/** The fewest Miles a checkout can use. */
export const MILES_CHECKOUT_MIN = 100;

/**
 * When Miles don't cover the whole trip, at least this much stays on the
 * payment plan: $25 keeps a 5% deposit over Stripe Capital's $1 floor, every
 * installment over Stripe's 50¢ minimum, and Zero over its $10 minimum.
 */
export const MILES_MIN_PLAN_CENTS = 2500;

const wholeMiles = (value) => {
  const n = Math.floor(Number(value) || 0);
  return n > 0 ? n : 0;
};

/** A whole number given as a number or as plain digits; NaN for anything else. */
const wholeNumberOrNaN = (value) => {
  if (typeof value === 'number') return Number.isInteger(value) ? value : NaN;
  if (typeof value === 'string' && /^[0-9]+$/.test(value)) return Number(value);
  return NaN;
};

/** Cents a number of Miles takes off a trip, rounded down to the cent. */
export function tripCentsForMiles(miles) {
  // 1.25¢ is 5/4 of a cent: integer math keeps 4 Miles at exactly 5¢.
  return Math.floor((wholeMiles(miles) * 5) / 4);
}

/** Cents a number of Miles is worth as an e-gift card. */
export function giftCardCentsForMiles(miles) {
  return wholeMiles(miles) * MILES_GIFT_CARD_CENTS_PER_MILE;
}

/** The fewest Miles worth at least `cents` off a trip. */
export function milesForTripCents(cents) {
  const c = Math.max(0, Math.ceil(Number(cents) || 0));
  return Math.ceil((c * 4) / 5);
}

/** The fewest Miles that pay for the whole trip. */
export function milesForWholeTrip(tripTotalCents) {
  return milesForTripCents(Math.max(0, Math.floor(Number(tripTotalCents) || 0)));
}

/** Do these Miles pay for the whole trip? */
export function coversWholeTrip(miles, tripTotalCents) {
  const total = Math.max(0, Math.floor(Number(tripTotalCents) || 0));
  return total > 0 && tripCentsForMiles(miles) >= total;
}

/** The most Miles that still leave the plan its minimum. */
function partialCapMiles(tripTotalCents) {
  const room = Math.max(0, Math.floor(Number(tripTotalCents) || 0)) - MILES_MIN_PLAN_CENTS;
  return room > 0 ? Math.floor((room * 4) / 5) : 0;
}

/**
 * The most Miles this checkout can use. If the balance pays for the whole
 * trip (and `allowWholeTrip`), that's exactly the Miles that cover it, and
 * the trip is booked with Miles alone. Otherwise it's the balance, capped so
 * the plan keeps MILES_MIN_PLAN_CENTS. Below the minimum it is 0, and the
 * Miles panel doesn't show.
 *
 * @param {{ balance: number, tripTotalCents: number, allowWholeTrip?: boolean }} p
 *   the trip total BEFORE Miles (after any discount code), travel only: a
 *   Faye premium is never paid with Miles. `allowWholeTrip` is false where a
 *   Miles-only booking isn't offered (a Flight + Hotel package, a legacy
 *   promo, a trip with travel insurance).
 */
export function maxCheckoutMiles({ balance, tripTotalCents, allowWholeTrip = true }) {
  const bal = wholeMiles(balance);
  const whole = milesForWholeTrip(tripTotalCents);
  if (allowWholeTrip && whole >= MILES_CHECKOUT_MIN && bal >= whole) return whole;
  const most = Math.min(bal, partialCapMiles(tripTotalCents));
  return most >= MILES_CHECKOUT_MIN ? most : 0;
}

/**
 * What a checkout spends: the customer's choice, clamped to what this trip
 * allows. `null`/`undefined` means the default, which is everything usable.
 * A choice under the minimum spends none; a choice short of the whole trip
 * leaves the plan its minimum.
 */
export function checkoutMilesToUse({ requested, balance, tripTotalCents, allowWholeTrip = true }) {
  const max = maxCheckoutMiles({ balance, tripTotalCents, allowWholeTrip });
  if (max === 0) return 0;
  if (requested === null || requested === undefined) return max;
  const want = wholeMiles(requested);
  if (want < MILES_CHECKOUT_MIN) return 0;
  if (coversWholeTrip(max, tripTotalCents) && want >= max) return max;
  const partial = Math.min(want, max, partialCapMiles(tripTotalCents));
  return partial >= MILES_CHECKOUT_MIN ? partial : 0;
}

/**
 * The fields a checkout's flight_data carries when it spends Miles. The
 * booking row keeps them, so the trip page, the desk and finance can all see
 * what Miles paid for. Empty when no Miles are used.
 */
export function checkoutMilesFields(miles) {
  const m = wholeMiles(miles);
  if (m < MILES_CHECKOUT_MIN) return {};
  return {
    miles_redeemed: m,
    miles_discount_cents: tripCentsForMiles(m),
    miles_cents_per_mile: MILES_TRIP_CENTS_PER_MILE,
  };
}

/**
 * The Miles a checkout's (or booking's) flight_data claims, or null when it
 * claims none. `valid` is false when the numbers don't agree with each other
 * or with the trip rate: the server refuses those.
 *
 * @returns {{ miles: number, discountCents: number, valid: boolean } | null}
 */
export function readCheckoutMiles(flightData) {
  const fd = flightData && typeof flightData === 'object' ? flightData : {};
  const rawMiles = fd.miles_redeemed;
  const rawCents = fd.miles_discount_cents;
  if ((rawMiles === undefined || rawMiles === null || rawMiles === 0)
    && (rawCents === undefined || rawCents === null || rawCents === 0)) {
    return null;
  }
  // Whole numbers only, as a number or plain digits: the booking triggers
  // read miles_redeemed as digits (migration 162), so "4000.0" or " 4000"
  // would pass here yet read as no Miles there.
  const miles = wholeNumberOrNaN(rawMiles);
  const discountCents = wholeNumberOrNaN(rawCents);
  const valid = Number.isInteger(miles)
    && miles >= MILES_CHECKOUT_MIN
    && Number.isInteger(discountCents)
    && discountCents === tripCentsForMiles(miles);
  return {
    miles: Number.isFinite(miles) ? miles : 0,
    discountCents: Number.isFinite(discountCents) ? discountCents : 0,
    valid,
  };
}

/** Cents of slack for the browser's float math against the server's. */
const CAP_SLACK_CENTS = 2;

/**
 * The checkout-integrity check on a Miles claim, for the plan layer (always
 * enforced) of the payment-plan rails: the claim adds up at the trip rate,
 * and it leaves the plan at least MILES_MIN_PLAN_CENTS (a trip Miles cover
 * entirely is booked with Miles alone, never through a plan).
 * `travelTotalCents` is the total the request asks the rail to collect,
 * travel only (no Faye premium).
 *
 * @returns {Array<{ code: string, layer: 'plan', message: string, detail?: Record<string, unknown> }>}
 */
export function checkMilesClaim({ flightData, travelTotalCents }) {
  const claim = readCheckoutMiles(flightData);
  if (!claim) return [];
  if (!claim.valid) {
    return [{
      code: 'miles_invalid',
      layer: 'plan',
      message: 'The Miles on this checkout do not add up at the trip rate.',
      detail: {
        miles_redeemed: claim.miles,
        miles_discount_cents: claim.discountCents,
        expected_discount_cents: tripCentsForMiles(claim.miles),
      },
    }];
  }
  const remaining = Math.max(0, Math.round(Number(travelTotalCents) || 0));
  if (remaining + CAP_SLACK_CENTS < MILES_MIN_PLAN_CENTS) {
    return [{
      code: 'miles_plan_too_small',
      layer: 'plan',
      message: 'Miles leave less than a payment plan can carry; a trip they cover entirely is booked with Miles alone.',
      detail: { miles_redeemed: claim.miles, miles_discount_cents: claim.discountCents, remaining_cents: remaining, minimum_plan_cents: MILES_MIN_PLAN_CENTS },
    }];
  }
  return [];
}

/**
 * The check on a trip booked with Miles alone: the claim adds up at the
 * trip rate, and it is the fewest whole Miles that pay for the trip's total
 * as the server priced it (`expectedTotalCents`), within the browser's
 * rounding slack. Empty when it passes.
 *
 * @returns {Array<{ code: string, message: string, detail?: Record<string, unknown> }>}
 */
export function checkMilesOnlyClaim({ flightData, expectedTotalCents }) {
  const claim = readCheckoutMiles(flightData);
  const expected = Math.max(0, Math.round(Number(expectedTotalCents) || 0));
  if (!claim || !claim.valid) {
    return [{ code: 'miles_invalid', message: 'The Miles on this booking do not add up at the trip rate.', detail: { claim } }];
  }
  if (expected <= 0) {
    return [{ code: 'miles_price_unknown', message: 'The trip’s price could not be worked out.', detail: { expected_total_cents: expected } }];
  }
  const covers = tripCentsForMiles(claim.miles) + CAP_SLACK_CENTS >= expected;
  const notOver = claim.miles <= milesForTripCents(expected + CAP_SLACK_CENTS);
  if (!covers || !notOver) {
    return [{
      code: 'miles_total_mismatch',
      message: covers ? 'More Miles than the trip costs.' : 'The Miles don’t cover the trip.',
      detail: { miles_redeemed: claim.miles, miles_value_cents: tripCentsForMiles(claim.miles), expected_total_cents: expected, miles_needed: milesForTripCents(expected) },
    }];
  }
  return [];
}

/** What the customer sees when their Miles changed between the page and the payment. */
export const MILES_UNAVAILABLE_MESSAGE = 'Your Miles balance changed since this page loaded, so we didn’t charge your card. Please refresh and try again.';
